// Spec 26 time-attendance — 7 entity type contracts.
//
// The broadest SDK in Phase 6 — covers punch capture, schedules,
// PTO balances / requests, weekly timesheets, and jurisdiction-aware
// overtime / break policies. Composes hris-sdk (Employee + legal
// entity + work location drive policy selection), coordination-sdk
// (schedule shifts surface as CalendarEvent mirrors), and documents-
// sdk (optional kiosk audit-photo reference).
//
// Name conventions: camelCase in TypeScript; Drizzle/SQL side uses
// snake_case. All fields readonly — mutation via dedicated transition
// helpers (time-entry.ts, timesheet.ts, pto.ts, schedule.ts).

// ==========================================================================
// TIME ENTRY (entity_time_entry)
// ==========================================================================

export type TimeEntryStatus =
  | "pending"
  | "active"
  | "submitted"
  | "approved"
  | "locked"
  | "rejected"
  | "adjusted"
  | "voided";

/** How the entry was captured. */
export type TimeEntryKind = "punch_pair" | "duration" | "imported" | "manual_adjustment";

/** Device / origin of the entry. */
export type TimeEntrySource =
  | "web"
  | "mobile"
  | "kiosk"
  | "api"
  | "manager_entry"
  | "import";

export type BreakKind = "meal" | "rest" | "unpaid";

export interface BreakRecord {
  readonly kind: BreakKind;
  readonly startAt: string;
  readonly endAt: string;
}

/** Pay code applied per policy. */
export type PayCode =
  | "regular"
  | "ot1"
  | "ot2"
  | "holiday"
  | "sick"
  | "pto"
  | "meal_premium"
  | "rest_premium"
  | "unpaid";

/** Geofence validation result at punch time. */
export type { GeofenceMatch, GeofenceResult } from "./geofence-policy.ts";
import type { GeofenceMatch } from "./geofence-policy.ts";

export interface TimeEntry {
  readonly entryId: string;
  readonly tenantId: string;
  readonly employeeId: string;
  readonly employmentId: string;
  readonly kind: TimeEntryKind;
  readonly startAt: string;
  readonly endAt?: string;
  /** Derived from start/end for punch_pair; provided directly for duration. */
  readonly durationMinutes?: number;
  readonly timezone: string;
  readonly source: TimeEntrySource;
  readonly locationId?: string;
  readonly geofenceMatch?: GeofenceMatch;
  readonly shiftId?: string;
  readonly projectCode?: string;
  readonly costCenterCode?: string;
  readonly departmentId?: string;
  readonly payCode?: PayCode;
  readonly breaks?: readonly BreakRecord[];
  readonly status: TimeEntryStatus;
  readonly editReason?: string;
  readonly notes?: string;
  readonly policyVersionId: string;
  /** Set when status=kiosk AND tenant enables KIOSK_PHOTO_AUDIT_ENABLED. */
  readonly kioskAuditPhotoDocumentId?: string;
  readonly kioskAuditPhotoCapturedAt?: string;
  readonly createdAt: string;
  readonly updatedAt: string;
}

// ==========================================================================
// SCHEDULE (entity_schedule)
// ==========================================================================

export type ScheduleStatus = "draft" | "published" | "archived";

export type ScheduleScopeKind = "team" | "location" | "department" | "legal_entity";

export interface Schedule {
  readonly scheduleId: string;
  readonly tenantId: string;
  readonly name: string;
  readonly scopeKind: ScheduleScopeKind;
  readonly scopeId: string;
  /** YYYY-MM-DD. */
  readonly periodStart: string;
  readonly periodEnd: string;
  readonly status: ScheduleStatus;
  readonly publishedAt?: string;
  readonly publishedBy?: string;
  readonly notes?: string;
  readonly createdAt: string;
  readonly updatedAt: string;
}

// ==========================================================================
// SHIFT (entity_shift)
// ==========================================================================

export type ShiftStatus = "open" | "assigned" | "swapped" | "cancelled" | "completed";

export interface BreakPlanEntry {
  readonly kind: BreakKind;
  readonly durationMinutes: number;
}

export interface Shift {
  readonly shiftId: string;
  readonly tenantId: string;
  readonly scheduleId: string;
  readonly assignedEmployeeId?: string;
  readonly role?: string;
  readonly locationId?: string;
  readonly startAt: string;
  readonly endAt: string;
  readonly timezone: string;
  readonly breakPlan?: readonly BreakPlanEntry[];
  readonly status: ShiftStatus;
  readonly swapRequestId?: string;
  readonly notes?: string;
  readonly createdAt: string;
  readonly updatedAt: string;
}

// ==========================================================================
// PTO BALANCE (entity_pto_balance)
// ==========================================================================

export type PtoBalanceStatus = "active" | "suspended" | "closed";

/**
 * The single list of accrual cadences a balance may name. The route schemas, the
 * field validator and the balance forms all read it, so a cadence added here is
 * accepted everywhere or nowhere — before it existed the form offered three
 * spellings the route rejected.
 */
export const ACCRUAL_PERIODS = [
  "per_pay_period",
  "monthly",
  "quarterly",
  "annual",
  "per_hour_worked",
] as const;

export type AccrualPeriod = (typeof ACCRUAL_PERIODS)[number];

/**
 * The cadences a CALENDAR sweep can advance on its own. `per_pay_period` and
 * `per_hour_worked` are deliberately absent: neither has a calendar anchor (one
 * follows the pay calendar, the other worked hours), so a periodic pass has
 * nothing to advance `next_accrual_at` by. A balance carrying one of those is
 * skipped, never accrued at a cadence nobody asked for — which is also why a
 * form must not offer them: a choice that never accrues reads as a bug.
 */
export const CALENDAR_ACCRUAL_PERIODS = ["monthly", "quarterly", "annual"] as const satisfies readonly AccrualPeriod[];

export type CalendarAccrualPeriod = (typeof CALENDAR_ACCRUAL_PERIODS)[number];

export interface PtoBalance {
  readonly balanceId: string;
  readonly tenantId: string;
  readonly employeeId: string;
  readonly policyId: string;
  /** Decimal hours — rendered in whole + partial hours (e.g., 62.5 = 62h 30min). */
  readonly balanceHours: number;
  readonly accrualRateHoursPerPeriod?: number;
  readonly accrualPeriod?: AccrualPeriod;
  /** Maximum balance cap — accrual halts at cap per policy. */
  readonly capHours?: number;
  readonly carryoverHours?: number;
  readonly lastAccrualAt?: string;
  readonly nextAccrualAt?: string;
  readonly status: PtoBalanceStatus;
  readonly notes?: string;
}

// ==========================================================================
// PTO REQUEST (entity_pto_request)
// ==========================================================================

export type PtoRequestStatus =
  | "draft"
  | "submitted"
  | "approved"
  | "rejected"
  | "cancelled"
  | "taken";

export interface PartialDaySpec {
  /** YYYY-MM-DD. */
  readonly date: string;
  /** Local wall-clock HH:MM, whole minutes; overnight windows are forbidden. */
  readonly startTime: string;
  readonly endTime: string;
}

export interface PtoRequest {
  readonly requestId: string;
  readonly tenantId: string;
  readonly employeeId: string;
  readonly policyId: string;
  /** YYYY-MM-DD. */
  readonly startDate: string;
  readonly endDate: string;
  readonly hours: number;
  readonly partialDays?: readonly PartialDaySpec[];
  readonly reason?: string;
  readonly status: PtoRequestStatus;
  readonly approverChain?: readonly string[];
  readonly approvalRequestId?: string;
  readonly coverageSuggestions?: Readonly<Record<string, unknown>>;
  readonly submittedAt?: string;
  readonly decidedAt?: string;
  readonly cancelledAt?: string;
  readonly takenAt?: string;
  readonly notes?: string;
}

// ==========================================================================
// TIMESHEET (entity_timesheet)
// ==========================================================================

export type TimesheetStatus = "open" | "submitted" | "approved" | "locked" | "reopened";

export type WeekStartDay =
  | "monday"
  | "tuesday"
  | "wednesday"
  | "thursday"
  | "friday"
  | "saturday"
  | "sunday";

export type TimesheetExceptionKind =
  | "missed_punch"
  | "unapproved_ot"
  | "missed_meal_break"
  | "short_meal_break"
  | "late_meal_break"
  | "missed_rest_break"
  | "geofence_override"
  | "post_lock_edit"
  // LT-03: worked hours on a day of approved (paid or unpaid) leave. Warning
  // only — it never blocks approve; the manager fixes the leave or the entries.
  | "leave_day_worked";

export interface TimesheetException {
  readonly kind: TimesheetExceptionKind;
  readonly entryId?: string;
  /** Tenant-local day the exception belongs to, for day-scoped kinds. */
  readonly date?: string;
  readonly description?: string;
}

export interface Timesheet {
  readonly timesheetId: string;
  readonly tenantId: string;
  readonly employeeId: string;
  readonly employmentId: string;
  /** YYYY-MM-DD. */
  readonly periodStart: string;
  readonly periodEnd: string;
  readonly weekStartDay: WeekStartDay;
  readonly totalRegularHours: number;
  readonly totalOtHours: number;
  readonly totalDoubleTimeHours: number;
  readonly totalHolidayHours: number;
  readonly totalPtoHours: number;
  readonly totalUnpaidHours: number;
  readonly exceptions?: readonly TimesheetException[];
  readonly status: TimesheetStatus;
  readonly employeeSignedOffAt?: string;
  readonly managerApprovedAt?: string;
  readonly lockedAt?: string;
  readonly payrollBatchRef?: string;
  readonly policyVersionId: string;
  readonly notes?: string;
}

// ==========================================================================
// TIME POLICY (entity_time_policy)
// ==========================================================================

export type TimePolicyStatus = "draft" | "active" | "archived";

export type TimePolicyKind =
  | "overtime"
  | "meal_break"
  | "rest_break"
  | "rounding"
  | "geofence"
  | "schedule_compliance"
  | "kiosk_access"
  | "time_off";

export interface TimePolicy {
  readonly timePolicyId: string;
  readonly tenantId: string;
  readonly platformPolicyId: string;
  readonly kind: TimePolicyKind;
  readonly jurisdictions: readonly string[];
  readonly parameters: Readonly<Record<string, unknown>>;
  /** YYYY-MM-DD. */
  readonly effectiveFrom: string;
  readonly effectiveTo?: string;
  readonly status: TimePolicyStatus;
  readonly description?: string;
}
